// İSG uzmanı portalı — Firma Onay-Routing ayarı (Kontrol aşaması; api-contract §31.2). Her belge
// türü için hangi departman(lar)ın onaylayacağını + eskalasyon süresi/departmanını tanımlar. Liste
// (belge türü + taraf + atanmış departman rozetleri + eskalasyon; sayfalı + `entity` filtresi) +
// ekle/düzenle (ApprovalRoutingFormModal) + kaldır (onay). Ayarı olmayan belge türü → **varsayılan:
// yalnız İSG** (bilgi notu). Auth gate = `permit-types` (katalog alanıyla ortak). Belge türü kimliği
// = `DocumentTypeRef` (`entity`, `code`); etiket §27 kaydından (pasif türler dahil; yoksa ham kod).

import {
  Alert,
  Button,
  Card,
  CardContent,
  ConfirmDialog,
  type DocumentTypeRef,
  EmptyState,
  Pagination,
  type PermitApprovalRoutingDto,
  RowActionsMenu,
  SelectMenu,
  type SelectOption,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableMessageRow,
  TableRow,
  TableSkeletonRows,
  TableToolbar,
  TableToolbarFilter,
  Tag,
  permitApprovalRoutingApi,
  resolveApiErrorMessage,
  useDocumentTypes,
} from '@isgportal/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, Plus, Route, SearchX } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ShellHeaderAction } from '../../components/layout/ShellHeaderAction';
import { ApprovalRoutingFormModal } from '../../components/permit-types/ApprovalRoutingFormModal';
import { permitApprovalRoutingKeys } from '../../lib/permitApprovalRoutingKeys';
import { useTableDensity } from '../../lib/uiPrefs';

const PAGE_SIZE = 20;

/** `entity` filtre değeri → param (undefined = hepsi). */
function entityParam(value: string): DocumentTypeRef['entity'] | undefined {
  if (value === 'personnel' || value === 'vehicle') {
    return value;
  }
  return undefined;
}

/** Eskalasyon özeti: "{saat} sa sonra {departman}" veya tire. */
function EscalationCell({ routing }: { routing: PermitApprovalRoutingDto }) {
  const { t } = useTranslation();
  if (routing.escalationHours == null || routing.escalationDepartment == null) {
    return <span className="text-sm text-muted">—</span>;
  }
  return (
    <span className="text-sm text-fg">
      {t('approvalRouting.list.escalationSummary', {
        hours: routing.escalationHours,
        department: t(`permits.department.${routing.escalationDepartment}`),
      })}
    </span>
  );
}

export function ApprovalRoutingListPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { label } = useDocumentTypes();

  const [entityFilter, setEntityFilter] = useState('');
  const [page, setPage] = useState(1);
  const [message, setMessage] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editItem, setEditItem] = useState<PermitApprovalRoutingDto | null>(null);
  const [deleteItem, setDeleteItem] = useState<PermitApprovalRoutingDto | null>(null);

  const entityOptions: SelectOption[] = useMemo(
    () => [
      { value: '', label: t('approvalRouting.filter.allTypes') },
      { value: 'personnel', label: t('documentSchemas.entity.personnel') },
      { value: 'vehicle', label: t('documentSchemas.entity.vehicle') },
    ],
    [t],
  );

  const params = {
    entity: entityParam(entityFilter),
    page,
    pageSize: PAGE_SIZE,
  };

  const query = useQuery({
    queryKey: permitApprovalRoutingKeys.listParams(params),
    queryFn: () => permitApprovalRoutingApi.listPermitApprovalRouting(params),
    placeholderData: (prev) => prev,
  });

  const deleteMutation = useMutation({
    mutationFn: (uuid: string) => permitApprovalRoutingApi.deletePermitApprovalRouting(uuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: permitApprovalRoutingKeys.all() });
      setMessage(t('approvalRouting.list.deleted'));
      setDeleteItem(null);
    },
  });

  const rows = query.data?.items ?? [];
  const [density, setDensity] = useTableDensity();
  const hasActiveFilters = Boolean(entityFilter);
  function clearFilters() {
    setEntityFilter('');
    setPage(1);
  }

  /** Belge türü okunur etiketi: "{taraf} · {tür}" — aynı kod iki entity'de olabilir (`Insurance`). */
  function docLabel(item: DocumentTypeRef): string {
    return `${t(`documentSchemas.entity.${item.entity}`)} · ${label(item.entity, item.code)}`;
  }

  return (
    // Owner review B2 item 7 — kendi yerel Breadcrumb+PageHeader+`PermitCatalogTabs`'ı
    // (S33 Slice 4 QA fix'in eklediği) ARTIK YOK; kabuk (`PermitTypesShell.tsx`) tek gerçek
    // başlığı + sekmeleri taşır, bu sayfa yalnız kendi aksiyonunu `ShellHeaderAction` ile
    // portal'lar (Admin/Ayarlar/Ziyaretçiler'in üç kardeş sayfasıyla AYNI desen).
    <div className="flex flex-col gap-5">
      <ShellHeaderAction>
        <Button
          onClick={() => {
            setMessage(null);
            setCreateOpen(true);
          }}
        >
          <Plus className="h-4 w-4" aria-hidden="true" />
          {t('approvalRouting.list.new')}
        </Button>
      </ShellHeaderAction>

      {/* Varsayılan davranış bilgi notu (§31.2: ayarı olmayan tür yalnız İSG onayı bekler). */}
      <div className="flex items-start gap-2 rounded-md border border-border bg-surface-muted p-3 text-sm text-muted">
        <Info className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" />
        <span>{t('approvalRouting.defaultNote')}</span>
      </div>

      {message ? <Alert tone="success">{message}</Alert> : null}

      <Card className="carbon:p-0">
        <CardContent className="flex flex-col gap-3 carbon:gap-0 carbon:p-0">
          <TableToolbar
            summary={
              query.data
                ? t('approvalRouting.list.count', { count: query.data.filteredTotal })
                : undefined
            }
            density={density}
            onDensityChange={setDensity}
            densityLabel={t('common.compactRows')}
            filters={
              <TableToolbarFilter>
                <SelectMenu
                  ariaLabel={t('approvalRouting.filter.entity')}
                  placeholder={t('approvalRouting.filter.allTypes')}
                  options={entityOptions}
                  value={entityFilter}
                  onValueChange={(value) => {
                    setEntityFilter(value);
                    setPage(1);
                  }}
                />
              </TableToolbarFilter>
            }
          />

          <Table caption={t('approvalRouting.title')} density={density}>
            <TableHeader>
              <TableRow>
                <TableHead>{t('approvalRouting.list.col.documentType')}</TableHead>
                <TableHead>{t('approvalRouting.list.col.departments')}</TableHead>
                <TableHead>{t('approvalRouting.list.col.escalation')}</TableHead>
                <TableHead className="w-12">
                  <span className="sr-only">{t('permitTypes.common.actions')}</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody aria-busy={query.isPending || undefined}>
              {query.isPending ? (
                <TableSkeletonRows columns={4} />
              ) : query.isError ? (
                <TableMessageRow colSpan={4}>
                  {resolveApiErrorMessage(query.error, t)}
                </TableMessageRow>
              ) : rows.length === 0 ? (
                <TableMessageRow colSpan={4}>
                  {hasActiveFilters ? (
                    <EmptyState
                      icon={SearchX}
                      title={t('approvalRouting.list.noResults')}
                      description={t('common.noResults.body')}
                      action={
                        <Button variant="ghost" size="sm" onClick={clearFilters}>
                          {t('common.clearFilters')}
                        </Button>
                      }
                      role="status"
                    />
                  ) : (
                    <EmptyState
                      icon={Route}
                      title={t('approvalRouting.list.empty')}
                      description={t('approvalRouting.list.empty.body')}
                      action={
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setMessage(null);
                            setCreateOpen(true);
                          }}
                        >
                          <Plus className="h-4 w-4" aria-hidden="true" />
                          {t('approvalRouting.list.new')}
                        </Button>
                      }
                    />
                  )}
                </TableMessageRow>
              ) : (
                rows.map((item) => (
                  <TableRow key={item.uuid}>
                    <TableCell truncate={false} className="break-words carbon:h-auto carbon:py-2">
                      {/* Birincil "aç" = belge türü hücresi (S33 Slice 4 QA fix, defekt #4) —
                          ayrı bir görünüm rotası yok, "açmak" düzenleme modalını açmakla AYNI. */}
                      <button
                        type="button"
                        onClick={() => {
                          setMessage(null);
                          setEditItem(item);
                        }}
                        className="block text-left font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      >
                        {docLabel(item)}
                      </button>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {item.requiredDepartments.length === 0 ? (
                          <span className="text-sm text-muted">—</span>
                        ) : (
                          item.requiredDepartments.map((d) => (
                            <Tag key={d} color="blue">
                              {t(`permits.department.${d}`)}
                            </Tag>
                          ))
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <EscalationCell routing={item} />
                    </TableCell>
                    <TableCell className="w-12 px-2">
                      <RowActionsMenu
                        ariaLabel={t('approvalRouting.list.action.openMenu', {
                          name: docLabel(item),
                        })}
                        items={[
                          {
                            key: 'delete',
                            label: t('permitTypes.list.action.delete'),
                            destructive: true,
                            onSelect: () => {
                              setMessage(null);
                              setDeleteItem(item);
                            },
                          },
                        ]}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>

          {query.data ? (
            <Pagination
              page={page}
              pageSize={PAGE_SIZE}
              filteredTotal={query.data.filteredTotal}
              onPageChange={setPage}
              labels={{
                nav: t('permitTypes.pagination.nav'),
                previous: t('permitTypes.pagination.previous'),
                next: t('permitTypes.pagination.next'),
              }}
            />
          ) : null}
        </CardContent>
      </Card>

      <ApprovalRoutingFormModal
        open={createOpen}
        onOpenChange={setCreateOpen}
        existing={rows}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ApprovalRoutingFormModal
        open={editItem !== null}
        onOpenChange={(open) => !open && setEditItem(null)}
        routing={editItem}
        onSuccess={(msg) => setMessage(msg)}
      />
      <ConfirmDialog
        open={deleteItem !== null}
        onOpenChange={(open) => !open && setDeleteItem(null)}
        title={t('approvalRouting.delete.title')}
        body={t('approvalRouting.delete.body', {
          documentType: deleteItem ? docLabel(deleteItem) : '',
        })}
        confirmLabel={t('permitTypes.catalog.delete.confirm')}
        loading={deleteMutation.isPending}
        error={deleteMutation.isError ? deleteMutation.error : undefined}
        onConfirm={() => deleteItem && deleteMutation.mutate(deleteItem.uuid)}
      />
    </div>
  );
}
